// TopBar — Cut AL.
//
// Minimal top strip. Per huashu-design: serve the page, don't compete with it.
// Three slots: breadcrumb (left), CTA (middle), user menu (right). All
// restrained — no logo here (sidebar handles wordmark), no search bar (each
// page can mount its own filter), no notifications bell (we have /observability).

import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// Maps URL segment → human breadcrumb label. Matches sidebar IA.
const SEGMENT_LABEL: Record<string, string> = {
  '': 'Home',
  insights: 'Insights',
  roles: 'Manage agents',
  'test-chat': 'Test Chat',
  conversations: 'Conversations',
  orders: 'Orders',
  recurring: 'Recurring',
  'business-ops': 'Business Ops',
  documents: 'Documents',
  memory: 'Memory',
  knowledge: 'Knowledge Base',
  vault: 'Vault',
  catalog: 'Catalog',
  growth: 'Growth Coach',
  connections: 'Connections',
  team: 'Team',
  onboarding: 'Onboarding',
  settings: 'Settings',
  welcome: 'Welcome',
  receipt: 'Receipt',
  new: 'New',
};

// Top-level group each segment belongs to (for two-level breadcrumb).
const SEGMENT_GROUP: Record<string, string> = {
  insights: 'Dashboard',
  roles: 'Agents',
  'test-chat': 'Agents',
  conversations: 'Operations',
  orders: 'Operations',
  'business-ops': 'Operations',
  documents: 'Knowledge',
  memory: 'Knowledge',
  knowledge: 'Knowledge',
  vault: 'Knowledge',
  catalog: 'Knowledge',
  growth: 'Growth',
  connections: 'Setup',
  team: 'Setup',
  onboarding: 'Setup',
  settings: 'Setup',
};

function buildCrumbs(pathname: string): Array<{ label: string; to?: string }> {
  if (pathname === '/' || pathname === '') return [{ label: 'Home' }];
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; to?: string }> = [];
  const first = segments[0];
  if (SEGMENT_GROUP[first]) crumbs.push({ label: SEGMENT_GROUP[first] });
  let acc = '';
  for (const seg of segments) {
    acc += '/' + seg;
    const label = SEGMENT_LABEL[seg] ?? decodeURIComponent(seg);
    crumbs.push({ label, to: acc });
  }
  // last crumb has no link
  if (crumbs.length > 0) crumbs[crumbs.length - 1] = { label: crumbs[crumbs.length - 1].label };
  return crumbs;
}

function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const crumbs = buildCrumbs(location.pathname);
  const initial = (user?.email ?? '?').charAt(0).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-20 bg-bg/95 backdrop-blur border-b border-line">
      <div className="flex items-center justify-between px-4 sm:px-6 h-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs min-w-0 flex-1" aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1.5 min-w-0">
              {i > 0 && <span className="text-muted opacity-50">/</span>}
              {c.to ? (
                <Link to={c.to} className="text-muted hover:text-text transition-colors truncate">{c.label}</Link>
              ) : (
                <span className="text-text truncate">{c.label}</span>
              )}
            </span>
          ))}
        </nav>

        {/* Right cluster — search + Test in chat CTA + user */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
            className="hidden md:inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded border border-line text-muted hover:text-text hover:bg-panel-soft transition-colors"
            aria-label="Search"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
            <span>Search</span>
            <kbd className="text-[10px] bg-panel-soft px-1 py-0.5 rounded border border-line">⌘K</kbd>
          </button>
          <Link to="/test-chat" className="hidden sm:inline-flex text-xs px-3 py-1.5 rounded border border-line text-muted hover:text-text hover:bg-panel-soft transition-colors">
            Test in chat
          </Link>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((x) => !x)}
              className="w-8 h-8 rounded-full bg-panel-soft border border-line text-text text-xs font-semibold hover:border-accent transition-colors"
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              {initial}
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-10 w-48 bg-bg border border-line rounded shadow-glass py-1 z-30">
                <div className="px-3 py-2 border-b border-line">
                  <div className="text-xs text-text truncate">{user?.email}</div>
                  <div className="text-[10px] text-muted uppercase tracking-wider">{user?.role}</div>
                </div>
                <Link to="/settings" className="block px-3 py-1.5 text-xs text-text hover:bg-panel-soft transition-colors" onClick={() => setMenuOpen(false)}>Settings</Link>
                <Link to="/team" className="block px-3 py-1.5 text-xs text-text hover:bg-panel-soft transition-colors" onClick={() => setMenuOpen(false)}>Team</Link>
                <button onClick={handleLogout} className="block w-full text-left px-3 py-1.5 text-xs text-warn hover:bg-warn/10 transition-colors">Logout</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar;
